import React from "react";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { formatMinorAmount } from "@/constants/billingCatalog";

const MD_COLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3" };

function isRecommended(item, items) {
  if (items.some((row) => row.legacy_tier === 2)) {
    return item.legacy_tier === 2;
  }
  if (!items.length) return false;
  const sorted = [...items].sort(
    (a, b) => a.sort_order - b.sort_order || String(a.tariff_id).localeCompare(String(b.tariff_id))
  );
  const mid = sorted[Math.floor((sorted.length - 1) / 2)];
  return item.tariff_id === mid.tariff_id;
}

function ctaLabel(item) {
  if (item.checkout_action === "subscribe_disabled") {
    return "Checkout in this currency will be available when plan switching is enabled";
  }
  if (item.checkout_action === "switch_disabled") {
    return "Plan changes will be available in a later update";
  }
  if (item.checkout_action === "current") return "Current plan";
  if (item.checkout_action === "switch") {
    return `Switch — ${formatMinorAmount(item.amount_minor, item.currency)}`;
  }
  return `Subscribe — ${formatMinorAmount(item.amount_minor, item.currency)}`;
}

export default function BillingCatalog({
  catalog,
  onPlanAction,
  isSubmitting,
  submittingTariffId,
  errorMessage,
}) {
  const items = catalog?.items ?? [];
  const colClass = MD_COLS[Math.min(3, Math.max(1, items.length))];

  const handlePlanClick = (clicked) => {
    const clickable =
      clicked.checkout_action === "subscribe" ||
      clicked.checkout_action === "switch";
    if (!clickable || isSubmitting) return;
    onPlanAction(clicked.tariff_id);
  };

  return (
    <div data-testid="billing-catalog">
      <div className="text-center mb-10">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-3">
          Choose Your Digital Assistant Plan
        </h2>
        <p className="font-body text-sm md:text-base text-[#9CA3AF] max-w-2xl mx-auto leading-relaxed">
          All plans include CRM and calendar sync, instant lead qualification, and
          monthly AI credits for live conversations. No setup fee — subscribe and go live.
        </p>
      </div>

      {catalog?.matrix_fallback ? (
        <p className="mb-6 text-sm text-center text-[#6B7280]">
          Prices shown are for the United States until plans are published for your country.
        </p>
      ) : null}

      {items.length === 0 ? (
        <p className="text-sm text-center text-[#9CA3AF]">
          No plans are published for your billing country yet.
        </p>
      ) : (
        <div className={`grid grid-cols-1 gap-5 ${colClass}`}>
          {items.map((item) => {
            const recommended = isRecommended(item, items);
            const canClick =
              item.checkout_action === "subscribe" || item.checkout_action === "switch";
            const isLoading = Boolean(
              canClick && isSubmitting && submittingTariffId === item.tariff_id
            );
            const isDisabled =
              !canClick || isLoading || (isSubmitting && submittingTariffId !== item.tariff_id);

            return (
              <div
                key={item.tariff_id}
                className={`relative flex flex-col rounded-2xl border p-6 ${
                  recommended
                    ? "border-[#00D1FF]/40 bg-[#00D1FF]/5 shadow-[0_0_40px_rgba(0,209,255,0.08)]"
                    : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`billing-catalog-card-${item.tariff_id}`}
              >
                {recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#00D1FF] px-3 py-1 text-[10px] font-heading font-bold uppercase tracking-wider text-[#0F1115]">
                    Recommended
                  </span>
                )}

                <div className="mb-4">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#00D1FF] mb-1">
                    {item.badge}
                  </p>
                  <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                    {item.name}
                  </h3>
                </div>

                <p className="font-heading font-bold text-3xl text-[#F3F4F6] mb-1">
                  {formatMinorAmount(item.amount_minor, item.currency)}
                </p>
                {catalog?.tax_inclusive ? (
                  <p className="text-xs text-[#9CA3AF]">Tax included</p>
                ) : null}
                <div className="mb-6">
                  <p className="text-xs text-[#9CA3AF]">Billed monthly</p>
                  {item.checkout_action === "subscribe" &&
                  Number.isInteger(item.trial_days_remaining) &&
                  item.trial_days_remaining >= 1 ? (
                    <p className="text-xs text-[#9CA3AF]">
                      {`${item.trial_days_remaining}-day free trial`}
                    </p>
                  ) : null}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                    <Sparkles className="h-4 w-4 text-[#00D1FF] flex-shrink-0 mt-0.5" />
                    <span>{item.credits_included} AI credits / month</span>
                  </li>
                  {(item.features || []).map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-sm text-[#9CA3AF]"
                    >
                      <CheckCircle2 className="h-4 w-4 text-[#00D1FF] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => handlePlanClick(item)}
                  disabled={isDisabled}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 font-heading font-bold text-sm transition-colors duration-300 ${
                    isDisabled
                      ? "bg-[#00D1FF]/40 text-[#0F1115]/60 cursor-not-allowed"
                      : recommended
                      ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                      : "bg-white/10 text-[#F3F4F6] hover:bg-white/15"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Redirecting to Stripe…
                    </>
                  ) : (
                    ctaLabel(item)
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {errorMessage ? (
        <p
          className="mt-6 text-sm text-red-400 text-center"
          data-testid="billing-error-message"
          role="alert"
        >
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
